import React, { useEffect, useState } from 'react';
import { api, DocMeta, VaultNode } from './api';
import { Icon } from './icons';
import { BlockSuiteEditor, EditorMode } from './BlockSuiteEditor';

const KIND_ICON: Record<string, string> = {
  doc: '📄', file: '📎', kb_entry: '📚', diagram: '🗺️', excerpt: '💬', canvas: '📝',
};

export function ChannelDocsTab({
  conversationId,
  workspaceId,
  channels,
  onToast,
}: {
  conversationId: string;
  workspaceId?: string;
  channels?: { id: string; name?: string | null }[];
  onToast: (t: string) => void;
}): React.ReactElement {
  const [docs, setDocs] = useState<DocMeta[] | null>(null);
  const [vaultNodes, setVaultNodes] = useState<VaultNode[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [mode, setMode] = useState<EditorMode>('page');
  const [diagramOpen, setDiagramOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [linkModal, setLinkModal] = useState<VaultNode | null>(null);
  const [tab, setTab] = useState<'all' | 'docs' | 'files' | 'kb'>('all');

  useEffect(() => {
    let alive = true;
    setOpenId(null);
    setDocs(null);
    setVaultNodes([]);
    api.listDocs(conversationId)
      .then((d) => { if (alive) setDocs(d); })
      .catch(() => alive && setDocs([]));
    api.vaultChannelNodes(conversationId)
      .then((n) => { if (alive) setVaultNodes(n); })
      .catch(() => {});
    return () => { alive = false; };
  }, [conversationId]);

  const open = (id: string) => { setMode('page'); setOpenId(id); };

  const create = async () => {
    setBusy(true);
    try {
      const doc = await api.createDoc(conversationId, 'Untitled doc');
      setDocs((cur) => (cur ? [...cur, doc] : [doc]));
      open(doc.id);
      onToast('Doc created');
    } catch (ex: any) {
      window.alert(ex?.message || 'Could not create doc');
    } finally {
      setBusy(false);
    }
  };

  const linkToChannel = async (node: VaultNode, channelId: string) => {
    try {
      await api.vaultPinNode(channelId, node.id);
      onToast(`Linked "${node.title}" to channel`);
      setLinkModal(null);
    } catch {
      onToast('Already linked or failed');
      setLinkModal(null);
    }
  };

  if (openId) {
    const current = docs?.find((d) => d.id === openId);
    return (
      <div className="fx-tabpane fx-docs">
        <div className="fx-canvas-bar">
          <button type="button" className="fx-login-back" onClick={() => setOpenId(null)}>← All docs</button>
          <div className="fx-canvas-title">{current?.title || 'Document'}</div>
          <div className="fx-docs-modes">
            <button type="button" className={`fx-docs-mode${mode === 'page' ? ' is-active' : ''}`}
              onClick={() => setMode('page')} title="Document">
              <Icon name="compose" size={13} /> Doc
            </button>
            <button type="button" className={`fx-docs-mode${mode === 'edgeless' ? ' is-active' : ''}`}
              onClick={() => setMode('edgeless')} title="Whiteboard">
              <Icon name="grid" size={13} /> Whiteboard
            </button>
            <button type="button" className="fx-docs-mode fx-docs-mode-diagram"
              onClick={() => { setMode('edgeless'); setDiagramOpen(true); }}
              title="Insert a diagram">
              <Icon name="compose" size={13} /> Diagram
            </button>
          </div>
        </div>
        <BlockSuiteEditor
          docId={openId}
          mode={mode}
          diagramOpen={diagramOpen}
          onDiagramClose={() => setDiagramOpen(false)}
          workspaceId={workspaceId}
        />
      </div>
    );
  }

  if (docs === null) return <div className="fx-tabpane"><div className="fx-empty-small">Loading…</div></div>;

  const filteredNodes = tab === 'all' ? vaultNodes
    : tab === 'docs' ? vaultNodes.filter((n) => n.kind === 'doc')
    : tab === 'files' ? vaultNodes.filter((n) => n.kind === 'file')
    : vaultNodes.filter((n) => n.kind === 'kb_entry');

  return (
    <div className="fx-tabpane fx-docs">
      <div className="fx-canvas-bar">
        <div className="fx-canvas-title"><Icon name="compose" size={15} /> Vault</div>
        <button type="button" className="fx-sendbtn" onClick={create} disabled={busy}>
          <Icon name="plus" size={13} /> {busy ? 'Creating…' : 'New doc'}
        </button>
      </div>

      <div className="fx-vault-tabs">
        {(['all', 'docs', 'files', 'kb'] as const).map((t) => (
          <button key={t} type="button"
            className={`fx-vault-tab${tab === t ? ' is-active' : ''}`}
            onClick={() => setTab(t)}>
            {t === 'all' ? 'All' : t === 'docs' ? 'Docs' : t === 'files' ? 'Files' : 'Knowledge'}
          </button>
        ))}
      </div>

      {filteredNodes.length === 0 && docs.length === 0 ? (
        <div className="fx-canvas-empty">
          <div className="fx-empty-big">No items yet</div>
          <p>Documents, files, and knowledge entries linked to this channel appear here.
            Click <b>New doc</b> to create one, or link existing items from other channels.</p>
        </div>
      ) : (
        <div className="fx-docs-list">
          {(tab === 'all' || tab === 'docs') && docs.map((d) => {
            const vn = vaultNodes.find((n) => n.doc_id === d.id);
            return (
              <div key={d.id} className="fx-vault-item">
                <button type="button" className="fx-docs-item" onClick={() => open(d.id)}>
                  <span className="fx-vault-kind">📄</span>
                  <span className="fx-docs-item-title">{d.title || 'Untitled doc'}</span>
                  {d.updated_at && (
                    <span className="fx-docs-item-date">{new Date(d.updated_at).toLocaleDateString()}</span>
                  )}
                </button>
                {vn && (
                  <button type="button" className="fx-vault-link-btn" title="Link to another channel"
                    onClick={() => setLinkModal(vn)}>
                    <Icon name="link" size={13} />
                  </button>
                )}
              </div>
            );
          })}
          {filteredNodes.filter((n) => n.kind !== 'doc').map((n) => (
            <div key={n.id} className="fx-vault-item">
              <button type="button" className="fx-docs-item"
                onClick={() => n.file_url ? window.open(n.file_url, '_blank') : undefined}>
                <span className="fx-vault-kind">{KIND_ICON[n.kind] || '📌'}</span>
                <span className="fx-docs-item-title">{n.title}</span>
                <span className="fx-docs-item-date">{n.kind}</span>
              </button>
              <button type="button" className="fx-vault-link-btn" title="Link to another channel"
                onClick={() => setLinkModal(n)}>
                <Icon name="link" size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      {linkModal && channels && (
        <div className="fx-modal-backdrop" onClick={() => setLinkModal(null)}>
          <div className="fx-modal fx-dir" onClick={(e) => e.stopPropagation()}>
            <div className="fx-login-title">Link "{linkModal.title}" to channel</div>
            <div className="fx-docs-list" style={{ maxHeight: 300, overflow: 'auto' }}>
              {channels.filter((c) => c.id !== conversationId).map((c) => (
                <button type="button" key={c.id} className="fx-docs-item"
                  onClick={() => linkToChannel(linkModal, c.id)}>
                  <span className="fx-pre">#</span> {c.name || 'Unnamed'}
                </button>
              ))}
            </div>
            <div className="fx-modal-actions">
              <button type="button" className="fx-login-back" onClick={() => setLinkModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
